import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { Section } from "../Section/Section";
import { OptionsSearchType } from "@/types/OptionType";
import { getRelativePathFromLink } from "../../utils/url";
import styles from "./SearchResult.module.scss";
import formStyles from "@/modules/Form/Form.module.scss";
import buttonStyles from "../../components/Button/Button.module.scss";
import { customSanitize } from "../../utils/customSanitize";
import { truncateExcerpt } from "../../utils/truncateExcerpt";
import { convertDateFormat } from "@/utils/convertDateFormat";
import { Loader } from "../Loader/Loader";
import { Button } from "../Button/Button";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { Date as DateComponent } from "@aleksundshantu/aunds-utils-react/components";
import { LoadMorePagination } from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { Post } from "@aleksundshantu/schirdewan-standard-types/client";
import { getDataArrayGeneric } from "@/getData/getData";
import { postTypes } from "@/utils/constants";

// blacklisted pages/posts ids
const blacklist = [3, 5140, 5126, 5116, 5132];

interface SearchResultProps {
  searchOptions: OptionsSearchType;
}

type SearchResultType = {
  keyword: string;
  postsTotal: number;
  posts: Post[];
};

const getQueryString = (v: string | string[] | undefined) =>
  Array.isArray(v) ? (v[0] ?? "") : (v ?? "");

const getQueryNumber = (v: string | string[] | undefined, fallback = 1) => {
  const raw = getQueryString(v);
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

export const SearchResult = ({ searchOptions }: SearchResultProps) => {
  const { search_page_link } = searchOptions ?? {};
  const router = useRouter();
  const { asPath, locale, defaultLocale } = router;

  const activePath = asPath.match(/^([^?]+)/) || [];
  const link = search_page_link && getRelativePathFromLink(search_page_link);
  const isSearchPage = activePath[1] === link;

  const perPage = 12;

  const [page, setPage] = useState<number>(1);
  const [searchPhrase, setSearchPhrase] = useState<string>("");

  const [searchResult, setSearchResult] = useState<SearchResultType>({
    keyword: "",
    postsTotal: 0,
    posts: [],
  });

  const [loading, setLoading] = useState<boolean>(false);

  const mainRef = useRef<HTMLDivElement>(null);

  // Prevent stale responses from overwriting newer results
  const reqIdRef = useRef(0);

  const { getLabel, getIcon } = useGlobalContext();

  const hasEvents = postTypes.includes("events");
  const postTypeParam = !hasEvents ? "&post_type=blog" : "";

  const fetchSearch = async (
    keyword: string,
    effectivePage: number,
    withScroll: boolean,
  ) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);

    try {
      const { pageData } = await getDataArrayGeneric<SearchResultType>(
        `/aunds/search/?keyword=${encodeURIComponent(
          keyword,
        )}&page=${effectivePage}&per_page=${perPage}${postTypeParam}`,
        locale,
        defaultLocale,
      );

      // Ignore late responses
      if (reqId !== reqIdRef.current) return;

      const posts = (pageData.posts || []).filter(
        (post) => !blacklist.includes(post.id),
      );

      setSearchResult({
        keyword: pageData.keyword,
        postsTotal: pageData.postsTotal,
        posts,
      });

      if (withScroll && mainRef.current) {
        mainRef.current.scrollIntoView({ behavior: "smooth" });
      }
    } catch (error) {
      if (reqId !== reqIdRef.current) return;

      // Keep UI stable on errors

      console.error("Error fetching paginated post data", error);

      setSearchResult({ keyword: "", postsTotal: 0, posts: [] });
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  // Sync from URL -> state and fetch when URL changes
  useEffect(() => {
    if (!router.isReady || !isSearchPage) return;

    const keywordFromUrl = getQueryString(router.query.keyword).trim();
    const pageFromUrl = getQueryNumber(router.query.page, 1);

    setSearchPhrase(keywordFromUrl);
    setPage(pageFromUrl);

    if (!keywordFromUrl) {
      setSearchResult({ keyword: "", postsTotal: 0, posts: [] });
      setLoading(false);
      return;
    }

    fetchSearch(keywordFromUrl, pageFromUrl, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, isSearchPage, router.query.keyword, router.query.page]);

  if (!isSearchPage) return null;

  const rawLabel = getLabel("arialabel_search_button");
  const keywordForAria = searchPhrase.trim();

  const ariaLabel = keywordForAria
    ? rawLabel.replace('"$$keyword"', keywordForAria)
    : rawLabel.replace(' "$$keyword"', "");

  // Pagination handler: drive URL
  const handleSetPage = (nextPage: number) => {
    if (!router.isReady) return;

    const keyword =
      getQueryString(router.query.keyword).trim() || searchPhrase.trim();
    if (!keyword) return;

    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          keyword,
          page: nextPage,
          pt: router.query.pt ?? "search",
        },
      },
      undefined,
      { shallow: true, scroll: false },
    );
  };

  return (
    <Section
      className={`search-result ${styles["search-result"]} has-weiss-bg`}
      columnsClassName="is-multiline is-centered"
      disableColumns
      ref={mainRef}
    >
      <div className="columns is-centered">
        <div className="column is-12-tablet is-10-desktop is-8-fullhd">
          <form
            className={`${formStyles["form-builder"]}`}
            onSubmit={(e) => {
              e.preventDefault();

              const k = searchPhrase.trim();
              if (!k) return;

              router.push(
                {
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    keyword: k,
                    page: 1,
                    pt: router.query.pt ?? "search",
                  },
                },
                undefined,
                { shallow: true, scroll: false },
              );
            }}
          >
            <div
              className={
                formStyles["input-group-formbuilder"] +
                " " +
                formStyles["input"]
              }
            >
              <label
                htmlFor="search-page-input"
                className={`${formStyles["label"]} label ${styles["search-label"]}`}
              >
                {getLabel("search_input_label")}
              </label>

              <input
                id="search-page-input"
                className={`${styles["search-input"]} `}
                name="searchbar"
                placeholder={getLabel("search_placeholder")}
                aria-label={getLabel("search_input_aria_label")}
                autoComplete="on"
                autoFocus
                value={searchPhrase}
                onChange={(e) => setSearchPhrase(e.target.value)}
              />

              <button
                type="submit"
                aria-label={ariaLabel}
                className="button-reset input"
              >
                <CustomSVG
                  src={getIcon("search")}
                  width={28}
                  height={28}
                  wrapper="span"
                  aria-hidden="true"
                  className="is-flex"
                />
              </button>
            </div>
          </form>
        </div>
      </div>

      <ul
        style={{ minHeight: "20vh" }}
        className="columns columns is-multiline is-centered is-relative"
        aria-live="polite"
      >
        {Array.isArray(searchResult.posts) &&
          !loading &&
          searchResult.posts.map((post, index) => {
            const { link } = post;

            const date =
              post.type === "events" ? (
                <DateComponent
                  dateFormat={"dd. MMMM yyyy"}
                  date={
                    new Date(
                      convertDateFormat(post.acf?.header_events.start_date),
                    )
                  }
                />
              ) : (
                post.date
              );

            const title =
              post.type === "blog"
                ? post.acf?.header_post?.title || ""
                : post.type === "events"
                  ? post.acf?.header_events?.title || ""
                  : "";

            const excerpt =
              post.type === "blog"
                ? post.acf?.header_post?.excerpt || ""
                : post.type === "events"
                  ? post.acf?.header_events?.excerpt || ""
                  : "";

            return (
              <li
                key={index}
                className={`column is-12-mobile is-10-tablet is-8-fullhd`}
              >
                <article className={`${styles["search-result-item"]} is-box`}>
                  <header>
                    {post.type !== "page" && (
                      <span className="date">{date}</span>
                    )}

                    {title && (
                      <span
                        className={"card-title"}
                        dangerouslySetInnerHTML={{
                          __html: customSanitize(title),
                        }}
                      />
                    )}
                  </header>

                  {excerpt && (
                    <div
                      className={styles["excerpt"]}
                      dangerouslySetInnerHTML={{
                        __html: customSanitize(truncateExcerpt(excerpt, 256)),
                      }}
                    />
                  )}

                  <footer>
                    <Button
                      className="mt-2"
                      type="secondary"
                      href={link}
                      target="_self"
                    >
                      {getLabel("read_more_label")}
                    </Button>
                  </footer>
                </article>
              </li>
            );
          })}

        {Array.isArray(searchResult.posts) &&
          !loading &&
          searchPhrase.trim() !== "" &&
          searchResult.posts.length === 0 && (
            <span className={styles["no-data"]}>
              {getLabel("no_search_results")}
            </span>
          )}

        {loading && <Loader />}

        {searchResult.postsTotal > 0 && (
          <div className="column is-12">
            <LoadMorePagination
              currentPage={page}
              pageType="search"
              postsPerPage={perPage}
              postsTotal={searchResult.postsTotal}
              setPage={handleSetPage}
              setScrollEnabled={() => {}}
              buttonStyles={buttonStyles}
            />
          </div>
        )}
      </ul>
    </Section>
  );
};
